import i18n from '@/i18n';
import { sourcingAPI } from '@/utils/api';
import { convertImageToServerUrl, normalizePersistedImageUrl } from '@/utils/imageUrlUtils';
import { resolveNavIdToUploadSource } from '@/utils/imageSpaceIngestAspect';
import { toProcessableImageUrl } from '@/pages/ipe/workbench/utils/sourcingWorkflowSpace';
import { reviseListingIngestPendingTotal } from '@/pages/ipe/workbench/utils/listingIngestProgress';

const COMPOSITE_INGEST_SOURCES = new Set(['goods_composite', 'podProductDesign_composite']);
const DETAIL_BRIDGE_PLATFORM_KEY = 'ipe-detail-bridge-listing';

/**
 * 将详情页快捷联动选中的图片地址解析为可入库的服务端 URL。
 * @param {string} imageUrl 原图 / 预览 / data URL
 * @returns {Promise<string>}
 */
async function resolveServerImageUrlForListing(imageUrl) {
  const raw = String(imageUrl || '').trim();
  if (!raw) {
    throw new Error(i18n.t('workbench:ipeUtilError.imageUrlUnresolved'));
  }
  if (raw.startsWith('/uploads/') || raw.startsWith('uploads/')) {
    return normalizePersistedImageUrl(raw) || raw;
  }
  if (raw.startsWith('http') && !raw.startsWith('data:')) {
    const persisted = normalizePersistedImageUrl(raw);
    if (persisted) return persisted;
    return raw;
  }
  const data = raw.startsWith('data:') ? raw : await toProcessableImageUrl(raw);
  if (!data) {
    throw new Error(i18n.t('workbench:ipeUtilError.imageReadFailed'));
  }
  const server = await convertImageToServerUrl(data);
  if (!server) {
    throw new Error(i18n.t('workbench:ipeUtilError.imageUploadServerFailed'));
  }
  return normalizePersistedImageUrl(server) || server;
}

async function resolveServerUrlList(urls = []) {
  const unique = [];
  const seen = new Set();
  for (const raw of urls) {
    const src = String(raw || '').trim();
    if (!src || seen.has(src)) continue;
    seen.add(src);
    unique.push(src);
  }
  const resolved = await Promise.all(unique.map((src) => resolveServerImageUrlForListing(src)));
  const out = [];
  for (const server of resolved) {
    if (server && !out.includes(server)) out.push(server);
  }
  return out;
}

/** 渐进入库：每批条目数（减少 HTTP 往返） */
const DETAIL_BRIDGE_INGEST_CHUNK_SIZE = 8;
/** 解析计划条目时的并发数 */
const DETAIL_BRIDGE_RESOLVE_CONCURRENCY = 4;

async function mapWithConcurrency(items, limit, mapper) {
  const list = Array.isArray(items) ? items : [];
  const concurrency = Math.max(1, Math.min(Number(limit) || 1, list.length || 1));
  const results = new Array(list.length);
  let nextIndex = 0;
  async function worker() {
    while (nextIndex < list.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await mapper(list[index], index);
    }
  }
  await Promise.all(Array.from({ length: concurrency }, () => worker()));
  return results;
}

function chunkArray(list, size) {
  const out = [];
  const step = Math.max(1, Number(size) || 1);
  for (let i = 0; i < list.length; i += step) {
    out.push(list.slice(i, i + step));
  }
  return out;
}

async function resolveColorSlotsForListing(colorSlots = []) {
  const slots = Array.isArray(colorSlots) ? colorSlots : [];
  const resolved = [];
  for (const slot of slots) {
    const mainUrl = String(slot?.mainUrl || '').trim();
    if (!mainUrl) continue;
    const uploadedMain = await resolveServerImageUrlForListing(mainUrl);
    const detailUrls = await resolveServerUrlList(slot?.detailUrls || []);
    const detailNames = Array.isArray(slot?.detailNames) ? slot.detailNames : [];
    resolved.push({
      ...slot,
      mainUrl: uploadedMain,
      detailUrls,
      detailNames: detailUrls.map((url, index) => (
        detailNames[index] || `细节${index + 1}`
      )),
    });
  }
  return resolved;
}

function normalizeBridgeSources(sources = []) {
  return (Array.isArray(sources) ? sources : [])
    .map((s, index) => ({
      id: String(s?.id ?? `bridge-${index}`).trim(),
      name: String(s?.name || s?.id || `图片 ${index + 1}`).trim(),
      title: String(s?.title || s?.bridgeListingGroupTitle || '').trim(),
      url: String(s?.url || s?.previewUrl || s?.displayUrl || '').trim(),
      bridgeListingGroupKey: String(s?.bridgeListingGroupKey || '').trim(),
      bridgeListingGroupTitle: String(
        s?.bridgeListingGroupTitle || s?.title || '',
      ).trim(),
      listingRole: String(s?.listingRole || '').trim().toLowerCase(),
      printImageUrls: collectSourcePrintUrls(s),
      detailImageUrls: collectSourceDetailUrls(s),
      printImageNames: Array.isArray(s?.printImageNames) ? s.printImageNames : [],
      mainImageNames: Array.isArray(s?.mainImageNames) ? s.mainImageNames : [],
      colorSlots: Array.isArray(s?.colorSlots) ? s.colorSlots : [],
      imageId: String(s?.imageId || '').trim(),
    }))
    .filter((s) => s.url || (s.detailImageUrls && s.detailImageUrls.length > 0));
}

function buildDetailBridgeIngestContext(params = {}) {
  const {
    sources = [],
    sourceNavId = '',
    sourceTaskId = '',
    fromCropTaskId = '',
    pageUrl = '',
    pageTitle = '',
    omitTitles = false,
    promoteToLocalLibrary = true,
  } = params;

  const list = normalizeBridgeSources(sources);
  if (list.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.selectImagesFirst'));
  }

  const rawSource = resolveDetailBridgeListingRawSource(sourceNavId);
  const isPodDesign = String(sourceNavId || '').trim() === 'podProductDesign'
    || rawSource === 'podProductDesign_composite';
  const skipTitles = omitTitles || isPodDesign;
  const ingestRunId = Date.now();

  return {
    list,
    rawSource,
    isPodDesign,
    skipTitles,
    ingestRunId,
    sourceNavId,
    sourceTaskId,
    fromCropTaskId,
    pageUrl: pageUrl || (typeof window !== 'undefined' ? window.location.href : ''),
    pageTitle: pageTitle || 'IPE 详情快捷联动 · 商品库',
    promoteToLocalLibrary: promoteToLocalLibrary !== false,
  };
}

function buildPlannedIngestItem({
  groupSources,
  groupIndex,
  context,
}) {
  const {
    sourceNavId,
    sourceTaskId,
    fromCropTaskId,
    rawSource,
    isPodDesign,
    skipTitles,
    ingestRunId,
    promoteToLocalLibrary,
  } = context;
  const groupKey = groupSources[0]?.bridgeListingGroupKey || `group-${groupIndex}`;
  const bridgeIds = groupSources.map((s) => s.id).join(',');
  const rawColorSlots = groupSources.flatMap((s) => (
    Array.isArray(s.colorSlots) ? s.colorSlots : []
  ));
  const printCandidates = groupSources.flatMap((s) => s.printImageUrls || []);
  const printImageNames = groupSources.flatMap((s) => (
    Array.isArray(s.printImageNames) ? s.printImageNames : []
  )).filter(Boolean);
  // 禁止用图片/印花 name（如「13」）顶标题：无显式 AI/桥接标题时留空，交给自动规则 AI 文案
  const groupTitleRaw = groupSources.find((s) => s.bridgeListingGroupTitle)?.bridgeListingGroupTitle
    || groupSources.find((s) => s.title)?.title
    || '';
  const hasExplicitTitle = groupSources.some((s) => s.title || s.bridgeListingGroupTitle);
  const mainImageNames = groupSources.flatMap((s) => (
    Array.isArray(s.mainImageNames) ? s.mainImageNames : []
  )).filter(Boolean);

  return {
    groupIndex,
    groupKey,
    bridgeIds,
    rawColorSlots,
    printCandidates,
    printImageNames,
    groupTitleRaw,
    hasExplicitTitle,
    mainImageNames,
    groupSources,
    externalKey: `detail-bridge-${sourceNavId || 'unknown'}-${groupKey}-${ingestRunId}-${groupIndex}`,
    skipTitles,
    isPodDesign,
    rawSource,
    sourceNavId,
    sourceTaskId,
    fromCropTaskId,
    promoteToLocalLibrary,
  };
}

function buildDetailBridgeIngestPlan(params = {}) {
  const context = buildDetailBridgeIngestContext(params);
  const sourceGroups = groupBridgeSourcesForListingIngest(context.list);
  const plannedItems = sourceGroups.map((groupSources, groupIndex) => (
    buildPlannedIngestItem({ groupSources, groupIndex, context })
  ));
  return {
    context,
    plannedItems,
    expectedItemCount: plannedItems.length,
  };
}

export function estimateDetailBridgeIngestItemCount(sources = []) {
  const list = normalizeBridgeSources(sources);
  return groupBridgeSourcesForListingIngest(list).length;
}

export async function resolveDetailBridgeListingSources(sources = [], {
  listingEnrich = null,
} = {}) {
  const list = normalizeBridgeSources(sources);
  if (!listingEnrich || typeof listingEnrich !== 'object') {
    return list;
  }
  const kind = String(listingEnrich.kind || '').trim();
  if (kind === 'goods') {
    const { enrichGoodsDetailSourcesForPlatformListing } = await import(
      './enrichGoodsDetailSourcesForPlatformListing'
    );
    return enrichGoodsDetailSourcesForPlatformListing(list, listingEnrich.options || {});
  }
  if (kind === 'aiCopywriting') {
    const { enrichAiCopywritingSourcesForPlatformListing } = await import(
      './enrichAiCopywritingSourcesForPlatformListing'
    );
    return enrichAiCopywritingSourcesForPlatformListing(list, listingEnrich.options || {});
  }
  if (kind === 'crop') {
    const { enrichCropDetailSourcesForPlatformListing } = await import(
      './enrichCropDetailSourcesForPlatformListing'
    );
    return enrichCropDetailSourcesForPlatformListing(
      list,
      listingEnrich.options?.displayImageData || [],
    );
  }
  return list;
}

async function resolvePlannedIngestItem(planned) {
  const {
    groupIndex,
    groupKey,
    bridgeIds,
    rawColorSlots,
    printCandidates,
    printImageNames,
    groupTitleRaw,
    hasExplicitTitle,
    mainImageNames: initialMainImageNames,
    groupSources,
    externalKey,
    skipTitles,
    isPodDesign,
    rawSource,
    sourceNavId,
    sourceTaskId,
    fromCropTaskId,
    promoteToLocalLibrary = true,
  } = planned;

  let carouselUrls = [];
  let detailUrls = [];
  let colorSlots = [];
  let mainImageNames = [...(initialMainImageNames || [])];
  let uploadedPrintUrls = [];

  if (rawColorSlots.length > 0) {
    colorSlots = await resolveColorSlotsForListing(rawColorSlots);
    carouselUrls = [...new Set(colorSlots.map((slot) => slot.mainUrl).filter(Boolean))];
    detailUrls = [...new Set(colorSlots.flatMap((slot) => slot.detailUrls || []).filter(Boolean))];
    if (!mainImageNames.length) {
      mainImageNames = colorSlots.map((slot) => String(slot?.mainName || '').trim()).filter(Boolean);
    }
    uploadedPrintUrls = await resolveServerUrlList(printCandidates);
  } else {
    const mainUrls = [];
    const detailFromRoleUrls = [];

    for (let i = 0; i < groupSources.length; i += 1) {
      const sourceItem = groupSources[i];
      if (!sourceItem.url) continue;
      const primary = await resolveServerImageUrlForListing(sourceItem.url);
      if (!primary) {
        throw new Error(i18n.t('workbench:ipeUtilError.groupUploadFailed', { group: groupIndex + 1, index: i + 1 }));
      }
      const role = resolveSourceListingRole(sourceItem, isPodDesign);
      if (role === 'detail') detailFromRoleUrls.push(primary);
      else mainUrls.push(primary);
    }

    uploadedPrintUrls = await resolveServerUrlList(printCandidates);
    const uploadedExplicitDetails = await resolveServerUrlList(
      groupSources.flatMap((s) => s.detailImageUrls || []),
    );

    detailUrls = [...new Set([...detailFromRoleUrls, ...uploadedExplicitDetails].filter(Boolean))];
    carouselUrls = [...new Set(mainUrls.filter(Boolean))];

    if (carouselUrls.length === 0 && detailUrls.length > 0) {
      carouselUrls = [detailUrls[0]];
    }
    if (isPodDesign && detailUrls.length === 0 && carouselUrls.length > 0) {
      detailUrls = [...carouselUrls];
    }
  }

  if (carouselUrls.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.groupNoMainOrDetail', { group: groupIndex + 1 }));
  }

  const baseTitle = skipTitles
    ? ''
    : String(groupTitleRaw || '').trim().slice(0, 200);

  return {
    externalKey,
    title: baseTitle,
    primaryImageUrl: carouselUrls[0],
    imageUrls: carouselUrls,
    printImageUrls: uploadedPrintUrls,
    printImageNames,
    detailImageUrls: detailUrls,
    mainImageNames,
    colorSlots,
    listingText: sourceTaskId
      ? `来源：${sourceNavId || '详情页'} 任务 ${sourceTaskId}；轮播 ${carouselUrls.length} 张；详情图 ${detailUrls.length} 张；印花 ${uploadedPrintUrls.length} 张；图片：${bridgeIds}`
      : `来源：${sourceNavId || '详情页'} 快捷联动；轮播 ${carouselUrls.length} 张；详情图 ${detailUrls.length} 张；印花 ${uploadedPrintUrls.length} 张；图片：${bridgeIds}`,
    raw: {
      source: rawSource,
      ...(promoteToLocalLibrary ? {} : { skipListingAutoRule: true }),
      sourceNavId: sourceNavId || null,
      sourceTaskId: sourceTaskId || null,
      fromCropTaskId: fromCropTaskId || null,
      bridgeImageIds: groupSources.map((s) => s.id),
      bridgeListingGroupKey: groupKey,
      carouselCount: carouselUrls.length,
      printImageUrls: uploadedPrintUrls,
      printImageNames,
      detailImageUrls: detailUrls,
      mainImageNames,
      colorSlots,
      imageRoles: {
        primary: colorSlots.length > 0 || carouselUrls.length > 0 ? 'composite' : 'detail',
        detail: detailUrls.length > 0 ? 'detail' : 'none',
        print: uploadedPrintUrls.length > 0 ? 'print_result' : 'none',
        title: skipTitles || !hasExplicitTitle
          ? 'none'
          : 'ai_copywriting_or_explicit',
      },
    },
    carouselCount: carouselUrls.length,
  };
}

function buildIngestBatchPayload(context, {
  batchKey = '',
  items = [],
  expectedItemCount = 0,
  finalize = false,
  bindListingAutoRule = false,
  listingAutoRuleId = null,
} = {}) {
  const payload = {
    platformKey: DETAIL_BRIDGE_PLATFORM_KEY,
    pageUrl: context.pageUrl,
    pageTitle: context.pageTitle,
    sourceSite: 'IPE',
    pluginVersion: 'detail-tool-bridge',
    rawPageSnapshot: null,
    items,
    finalize,
  };
  if (batchKey) payload.batchKey = batchKey;
  if (expectedItemCount > 0) payload.expectedItemCount = expectedItemCount;
  if (bindListingAutoRule) {
    payload.bindListingAutoRule = true;
    payload.listingAutoRuleId = listingAutoRuleId ?? null;
  }
  return payload;
}

async function createDetailBridgeListingBatch(context, expectedItemCount, autoRuleOptions = {}) {
  const ingestRes = await sourcingAPI.ingestBatch(buildIngestBatchPayload(context, {
    expectedItemCount,
    finalize: false,
    bindListingAutoRule: !!autoRuleOptions.bindListingAutoRule,
    listingAutoRuleId: autoRuleOptions.listingAutoRuleId ?? null,
  }));
  const batchKey = ingestRes?.batch?.batchKey;
  if (!batchKey) {
    throw new Error(i18n.t('workbench:listingManual.ingestNoBatch'));
  }
  return batchKey;
}

async function submitDetailBridgeIngestChunk(context, {
  batchKey,
  items,
  expectedItemCount = 0,
  finalize = false,
}) {
  await sourcingAPI.ingestBatch(buildIngestBatchPayload(context, {
    batchKey,
    items,
    expectedItemCount,
    finalize,
  }));
}

function mergeIngestedRowsWithPlan(listedRows = [], plannedItems = []) {
  const plannedList = Array.isArray(plannedItems) ? plannedItems : [];
  if (!plannedList.length) return Array.isArray(listedRows) ? listedRows : [];

  // 只回传本批计划内的条目，避免渐进入库 listItems 拉到整批后被重复累加
  const matched = [];
  const usedIndexes = new Set();
  const allowIndexFallback = listedRows.length === plannedList.length;
  plannedList.forEach((planned, plannedIndex) => {
    const key = String(planned?.externalKey || '').trim();
    let rowIndex = -1;
    if (key) {
      rowIndex = listedRows.findIndex((item, index) => (
        !usedIndexes.has(index)
        && String(item?.externalKey || item?.external_key || '').trim() === key
      ));
    }
    if (rowIndex < 0 && allowIndexFallback && !usedIndexes.has(plannedIndex)) {
      rowIndex = plannedIndex;
    }
    if (rowIndex < 0) return;
    usedIndexes.add(rowIndex);
    const row = listedRows[rowIndex];
    const detailImageUrls = Array.isArray(row?.detailImageUrls) && row.detailImageUrls.length > 0
      ? row.detailImageUrls
      : (planned.detailImageUrls || []);
    const printImageUrls = Array.isArray(row?.printImageUrls) && row.printImageUrls.length > 0
      ? row.printImageUrls
      : (planned.printImageUrls || []);
    const imageUrls = Array.isArray(row?.imageUrls) && row.imageUrls.length > 0
      ? row.imageUrls
      : (planned.imageUrls || []);
    const colorSlots = Array.isArray(row?.colorSlots) && row.colorSlots.length > 0
      ? row.colorSlots
      : (planned.colorSlots || []);
    const printImageNames = Array.isArray(row?.printImageNames) && row.printImageNames.length > 0
      ? row.printImageNames
      : (planned.printImageNames || []);
    const mainImageNames = Array.isArray(row?.mainImageNames) && row.mainImageNames.length > 0
      ? row.mainImageNames
      : (planned.mainImageNames || []);
    matched.push({
      ...row,
      title: row?.title || planned.title,
      primaryImageUrl: row?.primaryImageUrl || planned.primaryImageUrl,
      imageUrls,
      printImageUrls,
      printImageNames,
      detailImageUrls,
      mainImageNames,
      colorSlots,
      raw: {
        ...(row?.raw && typeof row.raw === 'object' ? row.raw : {}),
        ...(planned.raw && typeof planned.raw === 'object' ? planned.raw : {}),
        detailImageUrls,
        printImageUrls,
        printImageNames,
        mainImageNames,
        colorSlots,
      },
    });
  });
  return matched;
}

async function fetchIngestedRowsForPlan(batchKey, plannedItems = []) {
  const itemsRes = await sourcingAPI.listItems({
    batchKey,
    page: 1,
    pageSize: Math.max(plannedItems.length, 20),
  });
  const listedRows = Array.isArray(itemsRes?.items) ? itemsRes.items : [];
  return mergeIngestedRowsWithPlan(listedRows, plannedItems);
}

/**
 * 根据来源功能 navId 推断商品库 raw.source 字段。
 * @param {string} sourceNavId 详情页当前功能 id
 * @returns {string}
 */
export function resolveDetailBridgeListingRawSource(sourceNavId) {
  const id = String(sourceNavId || '').trim();
  if (!id) return 'detail_tool_bridge';
  if (id === 'goods') return 'goods_composite';
  if (id === 'podProductDesign') return 'podProductDesign_composite';
  if (id === 'thirdPartyReceive') return 'thirdPartyReceive';
  const base = resolveNavIdToUploadSource(id);
  if (!base) return 'detail_tool_bridge';
  if (COMPOSITE_INGEST_SOURCES.has(base)) return base;
  if (base === 'cropDetail') return base;
  if (base.endsWith('_result')) return base;
  return `${base}_result`;
}

/**
 * 按 bridgeListingGroupKey 将联动图片源分组（同组 → 一条商品的多张轮播图）。
 * @param {object[]} sources
 * @returns {object[][]}
 */
export function groupBridgeSourcesForListingIngest(sources = []) {
  const buckets = new Map();
  (Array.isArray(sources) ? sources : []).forEach((s, index) => {
    const groupKey = String(s?.bridgeListingGroupKey || '').trim()
      || `__solo__:${String(s?.id ?? index)}`;
    if (!buckets.has(groupKey)) buckets.set(groupKey, []);
    buckets.get(groupKey).push(s);
  });
  return [...buckets.values()];
}

function collectSourcePrintUrls(sourceItem) {
  const explicit = Array.isArray(sourceItem?.printImageUrls)
    ? sourceItem.printImageUrls
    : (Array.isArray(sourceItem?.printUrls) ? sourceItem.printUrls : []);
  return explicit.map((u) => String(u || '').trim()).filter(Boolean);
}

function collectSourceDetailUrls(sourceItem) {
  const explicit = Array.isArray(sourceItem?.detailImageUrls)
    ? sourceItem.detailImageUrls
    : (Array.isArray(sourceItem?.detailUrls) ? sourceItem.detailUrls : []);
  return explicit.map((u) => String(u || '').trim()).filter(Boolean);
}

function resolveSourceListingRole(sourceItem, isPodDesign) {
  const role = String(sourceItem?.listingRole || '').trim().toLowerCase();
  if (role === 'main' || role === 'detail') return role;
  return isPodDesign ? 'detail' : 'main';
}

/**
 * 渐进式入库：先创建批次，再逐条上传/写入并晋级到本地商品库。
 * @param {object} params 同 ingestDetailBridgeSourcesToPlatformListing
 * @param {object} [callbacks]
 * @param {(payload: { batchKey: string, expectedCount: number }) => void} [callbacks.onBatchReady]
 * @param {(payload: { batchKey: string, done: number, total: number, ingestedRows: object[] }) => void} [callbacks.onProgress]
 * @param {(payload: { batchKey: string, ingestedRows: object[], count: number, carouselImageCount: number }) => void} [callbacks.onComplete]
 */
export async function ingestDetailBridgeSourcesToPlatformListingProgressive(params = {}, callbacks = {}) {
  const {
    promoteToLocalLibrary = true,
    listingEnrich = null,
    bindListingAutoRule = false,
    listingAutoRuleId = null,
  } = params;
  const normalizedSources = normalizeBridgeSources(params.sources);
  if (normalizedSources.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.selectImagesFirst'));
  }

  const bootstrapContext = buildDetailBridgeIngestContext({ ...params, sources: normalizedSources });
  const estimateCount = Math.max(estimateDetailBridgeIngestItemCount(normalizedSources), 1);
  const batchKey = await createDetailBridgeListingBatch(bootstrapContext, estimateCount, {
    bindListingAutoRule,
    listingAutoRuleId,
  });
  callbacks.onBatchReady?.({
    batchKey,
    expectedCount: estimateCount,
  });

  const enrichedSources = await resolveDetailBridgeListingSources(normalizedSources, {
    listingEnrich,
  });
  if (enrichedSources.length === 0) {
    throw new Error(i18n.t('workbench:ipeUtilError.selectImagesFirst'));
  }

  const plan = buildDetailBridgeIngestPlan({ ...params, sources: enrichedSources });
  const { context, plannedItems } = plan;

  reviseListingIngestPendingTotal(batchKey, plan.expectedItemCount);

  const ingestedRows = [];
  let totalCarouselImages = 0;
  let autoRuleBoundOnPromote = false;
  const resolvedItems = await mapWithConcurrency(
    plannedItems,
    DETAIL_BRIDGE_RESOLVE_CONCURRENCY,
    async (planned) => resolvePlannedIngestItem(planned),
  );
  const chunks = chunkArray(resolvedItems, DETAIL_BRIDGE_INGEST_CHUNK_SIZE);
  let doneCount = 0;

  for (let chunkIndex = 0; chunkIndex < chunks.length; chunkIndex += 1) {
    const chunk = chunks[chunkIndex];
    totalCarouselImages += chunk.reduce((sum, item) => sum + (item.carouselCount || 0), 0);
    await submitDetailBridgeIngestChunk(context, {
      batchKey,
      items: chunk,
      expectedItemCount: plannedItems.length,
      finalize: chunkIndex === chunks.length - 1,
    });
    const rows = await fetchIngestedRowsForPlan(batchKey, chunk);
    if (promoteToLocalLibrary) {
      const sourcingItemIds = rows.map((row) => row?.id).filter((id) => id != null);
      if (sourcingItemIds.length > 0) {
        const promoteOptions = (!autoRuleBoundOnPromote && bindListingAutoRule)
          ? { bindListingAutoRule: true, listingAutoRuleId }
          : {};
        await sourcingAPI.promoteItems(sourcingItemIds, promoteOptions);
        autoRuleBoundOnPromote = true;
      }
    }
    ingestedRows.push(...rows);
    doneCount += chunk.length;
    callbacks.onProgress?.({
      batchKey,
      done: doneCount,
      total: plannedItems.length,
      ingestedRows: [...ingestedRows],
    });
  }

  const result = {
    batchKey,
    ingestedRows,
    count: plannedItems.length,
    carouselImageCount: totalCarouselImages,
  };
  callbacks.onComplete?.(result);
  return result;
}

/**
 * 详情快捷弹窗确认：将已选图片写入待上品并 promote 到商品库。
 * @param {object} params
 * @param {object[]} params.sources 联动弹窗中的图片源
 * @param {string} [params.sourceNavId] 来源功能 navId
 * @param {string} [params.sourceTaskId] 来源任务 id（主图/POD 等）
 * @param {string} [params.fromCropTaskId] 来源裁剪任务 id
 * @param {string} [params.pageUrl] 入库页 URL
 * @param {string} [params.pageTitle] 入库页标题
 * @param {boolean} [params.promoteToLocalLibrary=true] 为 false 时仅 ingested，不进入本地商品库
 * @param {boolean} [params.omitTitles=false] 为 true 时不写入标题（商品设计未做文案时）
 * @param {boolean} [params.progressive=true] 为 true 时先建批次再后台逐条入库
 * @returns {Promise<{ batchKey: string, ingestedRows: object[], count: number }>}
 */
export async function ingestDetailBridgeSourcesToPlatformListing(params = {}) {
  const { progressive = true, ...rest } = params;
  if (progressive) {
    return ingestDetailBridgeSourcesToPlatformListingProgressive(rest);
  }

  const plan = buildDetailBridgeIngestPlan(rest);
  const ingestItems = [];
  let totalCarouselImages = 0;
  for (const planned of plan.plannedItems) {
    const resolved = await resolvePlannedIngestItem(planned);
    totalCarouselImages += resolved.carouselCount || 0;
    ingestItems.push(resolved);
  }

  const ingestRes = await sourcingAPI.ingestBatch(buildIngestBatchPayload(plan.context, {
    items: ingestItems,
    finalize: true,
    bindListingAutoRule: !!rest.bindListingAutoRule,
    listingAutoRuleId: rest.listingAutoRuleId ?? null,
  }));
  const batchKey = ingestRes?.batch?.batchKey;
  if (!batchKey) {
    throw new Error(i18n.t('workbench:listingManual.ingestNoBatch'));
  }

  const ingestedRows = await fetchIngestedRowsForPlan(batchKey, ingestItems);
  const sourcingItemIds = ingestedRows.map((it) => it?.id).filter((id) => id != null);
  if (rest.promoteToLocalLibrary !== false && sourcingItemIds.length > 0) {
    await sourcingAPI.promoteItems(
      sourcingItemIds,
      rest.bindListingAutoRule
        ? { bindListingAutoRule: true, listingAutoRuleId: rest.listingAutoRuleId ?? null }
        : {},
    );
  }

  return {
    batchKey,
    ingestedRows,
    count: ingestItems.length,
    carouselImageCount: totalCarouselImages,
  };
}

/**
 * 跳转商品库批次详情（供 Toast 点击跳转；入库成功后不再自动调用）。
 * @param {object} params
 * @param {import('react-router-dom').NavigateFunction} params.navigate
 * @param {string} params.batchKey
 * @param {boolean} [params.recolor] 入库完成后自动打开 SPU 复色弹窗
 */
export function navigateAfterPlatformListingIngest({
  navigate,
  batchKey,
  recolor = false,
}) {
  if (typeof navigate !== 'function') return;
  const bk = String(batchKey || '').trim();
  const sp = new URLSearchParams();
  sp.set('activeNav', 'platformListing');
  if (bk) sp.set('listingBatchKey', bk);
  if (recolor) sp.set('listingRecolor', '1');
  navigate(`/ipe?${sp.toString()}`, {
    state: {
      activeNav: 'platformListing',
      ...(bk ? { platformListingBatchKey: bk } : {}),
    },
  });
}
